import type { StaffEarningsReport } from '@salonomy/types';
import { Card, Pill, Stat } from '@salonomy/ui';
import { useStaffEarningsReport, type EarningsRange } from '../../data/commission.js';
import { money, moneyWhole, percentFromBps } from '../../lib/format.js';

/**
 * WS9 §4 — commission/earnings columns on the takings + staff-KPI reports. The figures are COMPUTED by
 * the API over RECOGNISED revenue (real takings + voucher REDEMPTION revenue, NEVER voucher sales — see
 * ADR-0009 §Consequences); this component only DISPLAYS the server's rows. No money math happens here.
 *
 * Style matches the existing per-chair grid: a headline Card + one Card per staff member with mono-label
 * `Stat`s. A renter's chair rent (when recorded) shows beside their earnings so the period reads whole.
 */
export function EarningsSection({ range }: { range: EarningsRange }) {
  const enabled = Boolean(range.from && range.to);
  const { data: report, isLoading, isError } = useStaffEarningsReport(range, enabled);

  return (
    <div>
      <div className="section-title">Commission &amp; earnings</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 12 }}>
        Commission is paid on recognised revenue — real takings and voucher redemptions, never
        voucher or gift-card sales (a sold gift card is a liability, not income).
      </div>

      {isError ? (
        <Card>
          <div className="muted" style={{ fontSize: 13 }}>
            Couldn’t load earnings for this period.
          </div>
        </Card>
      ) : isLoading || !report ? (
        <Card>
          <div className="muted" style={{ fontSize: 13 }}>
            Loading earnings…
          </div>
        </Card>
      ) : (
        <EarningsBody report={report} />
      )}
    </div>
  );
}

function EarningsBody({ report }: { report: StaffEarningsReport }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card>
        <span className="slm-stat__label">Total earnings · this period</span>
        <div
          style={{
            fontFamily: 'var(--slm-font-display)',
            fontSize: 44,
            lineHeight: 1.05,
            marginTop: 4,
          }}
        >
          {moneyWhole(report.totalEarningsPence)}
        </div>
        <div className="muted">
          {moneyWhole(report.totalCommissionPence)} commission on{' '}
          {moneyWhole(report.totalRevenuePence)} recognised revenue
          {report.totalTipsPence > 0
            ? ` + ${moneyWhole(report.totalTipsPence)} tips (non-commissionable)`
            : ''}
        </div>
      </Card>

      {report.rows.length === 0 ? (
        <Card>
          <div className="muted" style={{ fontSize: 13 }}>
            No recognised revenue for any staff member in this period.
          </div>
        </Card>
      ) : (
        <div className="reports-grid">
          {report.rows.map((row) => (
            <Card key={row.staffId}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  marginBottom: 12,
                  gap: 8,
                }}
              >
                <span style={{ fontWeight: 600 }}>{row.staffName}</span>
                <Pill tone="soft">{percentFromBps(row.commissionRateBps)}</Pill>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <Stat label="Revenue" value={money(row.revenuePence)} />
                <Stat label="Commission" value={money(row.commissionPence)} />
                <Stat label="Tips" value={money(row.tipPence)} />
                <Stat label="Earnings" value={money(row.earningsPence)} />
              </div>
              {row.tipPence > 0 ? (
                <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
                  Tips {money(row.tipPence)} are the staff member&rsquo;s own gratuity — paid in
                  full, never commissionable.
                </div>
              ) : null}
              {row.chairRentPence > 0 ? (
                <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>
                  Chair rent {money(row.chairRentPence)}
                  {row.chairRentPeriod ? ` / ${row.chairRentPeriod}` : ''} recorded
                </div>
              ) : null}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
